import Cookies from 'js-cookie'
import { useEffect } from 'react'

import { useAppState } from 'context/AppContext'

const TRIGGER_SOURCE_COOKIE = 'trigger_source'

/** expires/maxAge yok → tarayıcı oturumu bitince silinir (session cookie). */
const TRIGGER_SOURCE_SESSION_COOKIE_OPTIONS: Cookies.CookieAttributes = {
  path: '/',
  sameSite: 'Lax',
}

function setTriggerSourceSessionCookie(value: string): void {
  Cookies.set(TRIGGER_SOURCE_COOKIE, value, TRIGGER_SOURCE_SESSION_COOKIE_OPTIONS)
}

/**
 * Affiliate gibi kalıcı Max-Age ile yazılmış trigger_source varsa,
 * değeri koruyarak session cookie'ye çevirir.
 */
function ensureTriggerSourceIsSessionCookie(): void {
  try {
    const existing = Cookies.get(TRIGGER_SOURCE_COOKIE)
    if (!existing) return
    setTriggerSourceSessionCookie(existing)
  } catch {
    // ignore
  }
}

/**
 * Cross-origin document.referrer çoğu zaman sorgu dizesi taşımaz.
 * Her harici kaynak için: referrer URL'sinde henüz sorgu yoksa ve landing'de
 * tıklama / kampanya parametreleri varsa, bunları referrer köküne ekler.
 * (landing-frontend Tracking ile aynı mantık)
 */
function mergeReferrerWithLandingAttributionSearch(referrerHref: string, landingSearch: string): string {
  if (!landingSearch) return referrerHref
  const hasAttributionParam = /(?:^|[?&])(?:gclid|gbraid|wbraid|gad_source|gad_campaignid)=/i.test(landingSearch)
  if (!hasAttributionParam) return referrerHref
  try {
    const u = new URL(referrerHref)
    if (u.search) return referrerHref
    const path = u.pathname || '/'
    return `${u.origin}${path}${landingSearch}`
  } catch {
    return referrerHref
  }
}

/** Eski sürüm `referrer|landing` çerezini yeni biçime taşır (eski bundle / kalıcı cookie). */
function normalizeLegacyPipeTriggerSourceCookie(): void {
  try {
    const existing = Cookies.get(TRIGGER_SOURCE_COOKIE)
    if (!existing?.includes('|')) return

    const pipe = existing.indexOf('|')
    const left = existing.slice(0, pipe).trim()
    const right = existing.slice(pipe + 1).trim()

    let search = ''
    if (right) {
      try {
        search = new URL(right).search || ''
      } catch {
        // ignore
      }
    }
    if (!search && typeof globalThis.window === 'object') {
      search = globalThis.location.search || ''
    }

    const fixed = mergeReferrerWithLandingAttributionSearch(left, search)
    setTriggerSourceSessionCookie(fixed)
  } catch {
    // ignore
  }
}

function getParameterByName(name: string): string {
  const escapedName = name.replaceAll('[', String.raw`\[`).replaceAll(']', String.raw`\]`)
  const regex = new RegExp(String.raw`[?&]` + escapedName + String.raw`=([^&#]*)`)
  const results = regex.exec(globalThis.location.search)
  if (results === null) return ''
  return decodeURIComponent(results[1].replaceAll('+', ' '))
}

function getRawReferrerHref(): string {
  try {
    return new URL(document.referrer).href
  } catch {
    return document.referrer
  }
}

function hasExternalReferrer(): boolean {
  if (typeof document?.referrer !== 'string' || document.referrer.length === 0) return false
  const pageOrigin = globalThis.location.protocol + '//' + globalThis.location.host
  return !document.referrer.includes(pageOrigin)
}

function collectTrackingParameters(): string[] {
  const parameters: string[] = []
  const affiliate = getParameterByName('affiliate')
  const gclid = getParameterByName('gclid')

  const landingHref = globalThis.window === undefined ? '' : globalThis.location.href

  if (hasExternalReferrer()) {
    const rawReferrerHref = getRawReferrerHref()
    const landingSearch = globalThis.window === undefined ? '' : globalThis.location.search || ''
    const attributionReferrer = mergeReferrerWithLandingAttributionSearch(rawReferrerHref, landingSearch)

    parameters.push('referrer=' + encodeURIComponent(attributionReferrer))

    const triggerSource = attributionReferrer

    parameters.push('trigger_source=' + encodeURIComponent(triggerSource))
    try {
      setTriggerSourceSessionCookie(triggerSource)
    } catch {
      // Cookie yazılamazsa sessizce devam et
    }
  } else if (landingHref && globalThis.location.search) {
    try {
      setTriggerSourceSessionCookie(landingHref)
    } catch {
      // Cookie yazılamazsa sessizce devam et
    }
  }

  if (affiliate?.length) {
    parameters.push('affiliate=' + affiliate)
  }

  if (gclid?.length) {
    parameters.push('gclid=' + gclid)
  }

  return parameters
}

function runAffiliateTracking(endpoint: string | undefined, market: string): void {
  normalizeLegacyPipeTriggerSourceCookie()
  ensureTriggerSourceIsSessionCookie()

  const parameters = collectTrackingParameters()

  if (!parameters?.length || !endpoint) return

  const trackingPath = market === 'tr' ? '/ucak-bileti/affiliate-tracking.json' : '/affiliate-tracking.json'
  const trackingUrl = `${endpoint}${trackingPath}?${parameters.join('&')}&t=${Date.now()}`
  fetch(trackingUrl)
}

type TrackingProps = {
  market: string
}

const Tracking = ({ market }: TrackingProps) => {
  const { endpoint } = useAppState()

  useEffect(() => {
    runAffiliateTracking(endpoint, market)
  }, [endpoint, market])

  return null
}

export default Tracking
